import { Chip, Table } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/data-state'

import { useInventoryUnits } from '../hooks'
import type { UnitGroup } from './inventory-format'
import { unitConversion, unitGroups } from './inventory-format'

/** What each family is for, under its heading. */
const familyDescriptions: Record<UnitGroup['family'], string> = {
  weight: 'Converts within its base — grams and kilos, ounces and pounds.',
  volume: 'Converts within its base — millilitres and litres.',
  count: 'Converts within its base — pieces and dozens.',
  own: 'Units you added. Each stands alone and converts into nothing else.',
}

/**
 * The units of measure, grouped by what they measure — Weight, Volume, Count,
 * then the business's own — each group a card of its own, with its bases
 * before the units built on them.
 *
 * Read-only. A system unit is not anyone's to edit, and a business's own units
 * are added through the service rather than here.
 */
export function UnitsPanel() {
  const { units, isLoading, error, retry } = useInventoryUnits()
  const groups = unitGroups(units)

  return (
    <>
      <ModuleIntro description="The units your stock can be measured in, grouped by what they measure. A recipe can use any unit that converts to its ingredient's — grams of flour stocked in kilos." />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="the units" error={error} onRetry={retry} />
      ) : groups.length === 0 ? (
        <EmptyState
          title="No units yet."
          description="The inventory service has not set up its units. They appear here once it has."
        />
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <UnitGroupSection key={group.family} group={group} />
          ))}
        </div>
      )}
    </>
  )
}

/** One family: its heading, how many it holds, and its units. */
function UnitGroupSection({ group }: { group: UnitGroup }) {
  const headingId = `units-${group.family}`

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-1">
        <h3 id={headingId} className="text-sm font-semibold tracking-tight">
          {group.label}{' '}
          <span className="text-muted font-normal tabular-nums">· {group.units.length}</span>
        </h3>
        <p className="text-muted text-xs">{familyDescriptions[group.family]}</p>
      </header>

      <Table>
        <Table.ScrollContainer>
          <Table.Content aria-labelledby={headingId}>
            <Table.Header>
              <Table.Column id="unit" isRowHeader>
                Unit
              </Table.Column>
              <Table.Column id="equals">Equals</Table.Column>
              <Table.Column id="uom">E-invoice code</Table.Column>
              <Table.Column id="source">Source</Table.Column>
              <Table.Column id="status">Status</Table.Column>
            </Table.Header>

            <Table.Body>
              {group.units.map((unit) => (
                <Table.Row key={unit.id} id={unit.id}>
                  <Table.Cell>
                    <span className="font-medium">{unit.code}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-muted tabular-nums">{unitConversion(unit)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-muted font-mono text-xs">{unit.uomCode ?? '—'}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-muted">{unit.isSystem ? 'System' : 'Yours'}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={unit.status === 'active' ? 'success' : 'default'}
                    >
                      {unit.status === 'active' ? 'Active' : 'Inactive'}
                    </Chip>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>
    </section>
  )
}
